import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'semantic-ui-react';
import { LANGUAGE_OPTIONS, redirectToUpperLevelOfTheNestedURL, useI18nContext } from '../../shared/services/i18n';
import { useParams } from 'react-router-dom';
import { useChangeLanguage } from '../../shared/hooks/useChangeLanguage';

type Option = { text: string; value: string };

const options: Option[] = LANGUAGE_OPTIONS.map(option => ({ text: option.label, value: option.value }));
const defaultOption = options[0];

const useActiveOption = () => {
  const i18nContext = useI18nContext();
  return options.find(option => option.value === i18nContext.language) ?? defaultOption;
};

const useSetLanguageFromURL = () => {
  const { language } = useParams<{ language?: string }>();
  const activeOption = useActiveOption();
  const changeLanguage = useChangeLanguage();

  const formattedURLLanguage = language?.toLowerCase();
  const supportedOption = options.find(option => option.value.toLowerCase() === formattedURLLanguage);
  const shouldApply = !!supportedOption && formattedURLLanguage !== activeOption.value;

  useEffect(() => {
    // A localized link like /iqc/zh-cn applies that language and, when logged in, syncs it to the
    // account so the URL choice and the stored preference stay in sync. Runs in an effect (not during
    // render) because persisting is async.
    if (shouldApply && supportedOption) {
      changeLanguage(supportedOption.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldApply, supportedOption?.value]);
};

interface LanguageSelectorProps {
  isUp?: boolean;
  className?: string;
}

export const LanguageSelector = ({ isUp = false, className = 'iqc__language-selector' }: LanguageSelectorProps) => {
  const activeOption = useActiveOption();
  const { language } = useParams<{ language?: string }>();
  const changeLanguage = useChangeLanguage();

  useSetLanguageFromURL();

  const [isOpen, setIsOpen] = useState(false);
  // Optimistically reflect the chosen language in the dropdown while the save + reload is in flight,
  // so the control feels responsive instead of frozen until the API responds.
  const [pendingValue, setPendingValue] = useState<string | null>(null);

  const isSaving = pendingValue !== null;
  const displayedOption = useMemo(
    () => (pendingValue && options.find(option => option.value === pendingValue)) || activeOption,
    [pendingValue, activeOption]
  );

  const onSelectLanguage = (option: Option) => async () => {
    if (isSaving || option.value === activeOption.value) {
      setIsOpen(false);
      return;
    }

    // Show the selection immediately and lock the control; the page reloads/redirects once the
    // persist + apply completes.
    setPendingValue(option.value);
    setIsOpen(false);

    try {
      // Persist (when logged in) + apply. Skip the reload when a URL language param is present; we
      // redirect to the upper level instead, which itself re-applies the language on the next render.
      await changeLanguage(option.value, !language);
      if (language) {
        redirectToUpperLevelOfTheNestedURL();
      }
    } catch {
      // Save failed before navigating away — revert the optimistic selection and re-enable.
      setPendingValue(null);
    }
  };

  return (
    <Dropdown
      className={`header__nav-auth-item competitions__dropdown ${className}`}
      icon="world"
      open={isOpen}
      text={displayedOption.text}
      disabled={isSaving}
      onClick={() => setIsOpen(!isOpen)}
      direction="right"
      defaultUpward={isUp}
    >
      <Dropdown.Menu>
        {options.map(option => (
          <Dropdown.Item onClick={onSelectLanguage(option)} className="competitions__dropdown" key={option.value}>
            <div className="competitions__dropdown-link">{option.text}</div>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};
